import "../../assets/css/icons.css";
import "../../assets/css/checkout.css";
import { useContext, useState, useEffect, useCallback, useRef } from "react";
import {
  CcFields,
  CheckoutData,
  FormFields,
  FormMethodTypes,
  LineItem,
  Product,
  TokenExIframe
} from "../../types";
import { WVUSForm } from "wvus-forms";
import FieldsGroup from "./FieldsGroup";
import FirstNameField from "./FirstNameField";
import EmailField from "./EmailField";
import LastNameField from "./LastNameField";
import PhoneField from "./PhoneField";
import CreditCardField from "./CreditCardField";
import ExpirationDateField from "./ExpirationDateField";
import AddressAutoComplete from "./AddressAutoComplete/AddressAutoComplete";
import AddressToggle from "./AddressToggle";
import AddressGroup from "./AddressGroup";
import AddressLine1Field from "./AddressLine1Field";
import AddressLine2Field from "./AddressLine2Field";
import CityField from "./CityField";
import StateField from "./StateField";
import PostalCodeField from "./PostalCodeField";
import SmsOptIn from "./SmsOptIn";
import SubmitMessage from "./SubmitMessage";
import SubmitButton from "./SubmitButton";
import MainStateContext from "../../state/mainStateContext";
import PaymentHeader from "./PaymentHeader";
import {
  getOrderApiEnv,
  toDigitsOnly,
  getCampaignVal,
  triggerEvent,
  triggerEventOnce,
  orderRedirect,
  storeOrderDetailLocalStorage,
  getBlueConicProfileId,
  CHOSEN_PRODUCT_ID,
  wvusLogger,
  getRecaptchaToken,
  scrollToElementIfNotInView,
  getQueryParam,
  setBrowserAutoFillUsed,
  setEhashCookieAsync,
  toTokenExErrorMessage,
  decideTokenExErrorAction,
  decideConfigApplication,
  getCheckoutTypeVal
} from "../../helpers";
import { AUTH_KEY_TTL_MS } from "../../hooks/useTokenConfig";

import { isPaymentAuthError } from "../../services/orderAPI";
import { StateContextType } from "../../types/state.types";
import { QuantityControl } from "./QuantityControl/QuantityControl";
import { HorizontalRule } from "./HorizontalRule";
import appSettings from "../../settings/appSettings";
import PhotoConsentOption from "../ChosenApp/PhotoConsentOption";
import TourOrderSummary from "../TourApp/TourOrderSummary";
import ChosenOrderSummary from "../ChosenApp/ChosenOrderSummary";
import { Say, PhraseKey, getPhrase } from "../../languages";
import { useRequestOrder } from "../../orderRequest/useOrderRequest";

/**
 * How long to wait for the TokenEx iframe to report `load` before recording it as stalled
 */
const TOKENEX_INIT_TIMEOUT_MS = 20 * 1000;

/** Point at which the iframe is treated as never coming. Long, because a false positive
 * means retracting an error from a field that was about to work. */
const TOKENEX_LOAD_DEADLINE_MS = 45 * 1000;

const TOKENEX_TOKENIZE_TIMEOUT_MS = 30 * 1000;

/** GA4 event_label per expiry outcome. not_expiry keeps the flat historical value. */
const EXPIRY_OUTCOME_LABELS = {
  not_expiry: "TokenEx",
  recoverable: "TokenEx:refresh_failed",
  budget_exhausted: "TokenEx:budget_exhausted",
  within_cooldown: "TokenEx:within_cooldown"
};

type Props = {
  formMethods: FormMethodTypes;
  tokenExConfigQuery: {
    data: object | null;
    status: "loading" | "success" | "error";
    exhausted?: boolean;
    configuredAt?: number;
    refresh: () => Promise<boolean>;
  };
  formState: {
    fields: FormFields;
    formValid: boolean;
  };
  opts: {
    checkoutId: string;
    mapProducts: (products: Product[], state: object) => [];
    mapLineItems: (products: Product[], state: object) => [];
  };
  ctaPhraseKey?: PhraseKey;
  designatedProductId?: string;
  onCheckoutLineItemsMap?: (lineItems: LineItem[], checkoutData: CheckoutData) => [];
  onCheckoutSuccess?: (data) => void;
  isKioskMode?: boolean;
};

export const Checkout = WVUSForm((props: Props) => {
  const {
    allowMultipleQuantity,
    enableCCAuth: allowAuth,
    showSmsPreference,
    showPhotoConsentOption = false
  } = appSettings;
  const {
    formMethods,
    formState,
    tokenExConfigQuery,
    opts,
    ctaPhraseKey = "generic.submission.cta",
    designatedProductId,
    onCheckoutLineItemsMap = (lineItems) => lineItems,
    onCheckoutSuccess = () => {},
    isKioskMode
  } = props;
  const {
    state,
    dispatch,
    setSubmitMessagePhraseKey,
    toggleOverlay,
    getProductQuantity,
    findProductById
  } = useContext(MainStateContext) as StateContextType;
  const { submitMessagePhraseKey, cobrand } = state;
  const { startOrder, duplicateOrderLookup, isRequesting } = useRequestOrder();

  const { step2 } = state;
  const [toggleFlag, setToggleFlag] = useState("off");
  const [formStatus, setFormStatus] = useState("ready"); // pending, ready, loading, success, error
  const [cardAlertMessagePhraseKey, setCardAlertMessagePhraseKey] = useState<PhraseKey>("");
  const [smsPreference, setSmsPreference] = useState(true); // true or false ( boolean)
  const [photoConsent, setPhotoConsent] = useState(showPhotoConsentOption);

  /* Credit Card Related */
  const refOnTokenize = useRef<(object) => void>(null);
  const refIframe = useRef<TokenExIframe>(null);
  const [secondInteraction, setSecondInteration] = useState(false);
  const [isCCValid, setIsCCValid] = useState(false);
  const [validationErrorCCPhraseKey, setValidationErrorCCPhraseKey] = useState<PhraseKey>("");
  const [cardType, setCardType] = useState("");
  const [tokenExErrorPhraseKey, setTokenExErrorPhraseKey] = useState(null);
  const [checkoutExpanded, setCheckoutExpanded] = useState(false);
  const [checkoutExpandedHeight, setCheckoutExpandedHeight] = useState("0");
  const [failedTokenizeRefreshCount, setFailedTokenizeRefreshCount] = useState(0);
  const expiryRefreshCountRef = useRef(0);
  const lastExpiryRefreshAtRef = useRef(0);
  const expiryRefreshInFlightRef = useRef(false);
  const isRequestingRef = useRef(false);
  const [appliedConfig, setAppliedConfig] = useState(null);
  /** State rather than a ref: the config effect must re-run on blur to apply what it held. */
  const [isCardFocused, setIsCardFocused] = useState(false);
  /** When the currently applied config was wired up, for judging whether its key is dead. */
  const appliedConfigAtRef = useRef(0);
  const orderInFlightRef = useRef(false);
  const lastRefreshSucceededRef = useRef(false);
  const pendingExpiryRecoveryRef = useRef(false);
  const tokenizeWatchdogRef = useRef(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [formChangeState, setFormChangeState] = useState({
    changedFields: new Set(),
    debounceTimeout: null
  });

  useEffect(() => {
    isRequestingRef.current = isRequesting;
  }, [isRequesting]);

  useEffect(() => {
    // Browsers do not reliably blur the focused element on a tab switch, and TokenEx may not
    // emit blur across it either so without this, focus taken before a 25 minute detour
    // still reads as true on return and defers the config that was fetched to recover
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setIsCardFocused(false);
        return;
      }

      resetExpiryRefreshBudget();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  const productsRef = useRef(state?.products);
  useEffect(() => {
    productsRef.current = state?.products;
  }, [state?.products]);

  useEffect(() => {
    if (tokenExConfigQuery?.exhausted) {
      setTokenExErrorPhraseKey("errors.tokenex_cc_unavailable");
      return;
    }

    setTokenExErrorPhraseKey((previous) =>
      previous === "errors.tokenex_cc_unavailable" ? null : previous
    );
  }, [tokenExConfigQuery?.exhausted]);

  const clearTokenizeWatchdog = () => {
    if (tokenizeWatchdogRef.current) {
      clearTimeout(tokenizeWatchdogRef.current);
      tokenizeWatchdogRef.current = null;
    }
  };

  const startTokenizeWatchdog = () => {
    clearTokenizeWatchdog();
    tokenizeWatchdogRef.current = setTimeout(() => {
      tokenizeWatchdogRef.current = null;
      orderInFlightRef.current = false;
      wvusLogger.log(
        `tokenEx tokenize produced no result within ${TOKENEX_TOKENIZE_TIMEOUT_MS}ms`,
        wvusLogger.getLogLevelNames().ERROR
      );
      toggleOverlay(false);
      setFormStatus("ready");
      setIsCCValid(false);
      setCardType("");
      setCardAlertMessagePhraseKey("errors.tokenex_technical");
      triggerEvent("error_view", {
        error_message: `TokenEx tokenize produced no result within ${TOKENEX_TOKENIZE_TIMEOUT_MS}ms`,
        error_type: "app_error",
        event_label: "TokenEx:tokenize_timeout",
        checkout_type: getCheckoutTypeVal(productsRef.current)
      });
    }, TOKENEX_TOKENIZE_TIMEOUT_MS);
  };

  useEffect(() => clearTokenizeWatchdog, []);

  const recordExpiryRefresh = () => {
    expiryRefreshCountRef.current += 1;
    lastExpiryRefreshAtRef.current = Date.now();
  };

  const resetExpiryRefreshBudget = () => {
    // Count only. The timestamp is a rate limit, not part of the budget and clearing it
    // would let every card re-entry get three attempts with no gap.
    expiryRefreshCountRef.current = 0;
  };

  /**
   * Applies a new config, which rebuilds iframe and discards whatever was typed.
   * Deferred during a submit: tokenize() reports through an event on the iframe instance,
   * so swapping mid-flight destroys the only path back
   */
  useEffect(() => {
    const incoming = tokenExConfigQuery?.data;
    const application = decideConfigApplication({
      incoming,
      applied: appliedConfig,
      formStatus,
      cardFocused: isCardFocused,
      appliedKeyExpired:
        appliedConfigAtRef.current > 0 && Date.now() - appliedConfigAtRef.current >= AUTH_KEY_TTL_MS
    });

    if (application === "none" || application === "deferred") {
      return;
    }

    setAppliedConfig(incoming);
    appliedConfigAtRef.current = tokenExConfigQuery?.configuredAt || Date.now();

    if (application === "initial") {
      return;
    }

    const hadCardState = isCCValid || !!cardType;

    setCardType("");
    setIsCCValid(false);

    if (hadCardState) {
      setCardAlertMessagePhraseKey("errors.tokenex_inactive");
      setValidationErrorCCPhraseKey("errors.tokenex_inactive");
    }

    if (pendingExpiryRecoveryRef.current) {
      pendingExpiryRecoveryRef.current = false;
      triggerEvent("error_view", {
        error_message: "TokenEx auth key refreshed",
        error_type: "app_error",
        event_label: "TokenEx:auth_key_refreshed",
        checkout_type: getCheckoutTypeVal(productsRef.current)
      });
    }

    setTokenExErrorPhraseKey(null);
    setFormStatus((previous) => (previous === "error" ? "ready" : previous));
  }, [tokenExConfigQuery?.data, formStatus, appliedConfig, isCardFocused, isCCValid, cardType]);

  const products = state?.products;
  const hasProduct = !!products?.length;
  const formEnabled = hasProduct && ["ready"].includes(formStatus) && formState.formValid;
  const isFormEnabled = formEnabled && isCCValid;
  const productQuantity = getProductQuantity(designatedProductId) || 1;

  useEffect(() => {
    const fields: FormFields = formState?.fields;

    if (fields?.address1?.value) {
      const eventLabel = toggleFlag === "on" ? "standard_address" : "one_line_address";
      triggerEventOnce("add_billing_address", { products: state.products, eventLabel: eventLabel });
    }
  }, [formState.fields, state.products, toggleFlag]);

  useEffect(() => {
    if (submitMessagePhraseKey && formState.formValid) {
      setSubmitMessagePhraseKey("");
    }
  }, [formState.fields, formState.formValid]);

  // Create a ref for the expando content
  const expandoContentRef = useRef<HTMLDivElement>(null);

  // Unified function to calculate height
  const updateExpandoHeight = useCallback(() => {
    window.requestAnimationFrame(() => {
      if (!expandoContentRef.current?.firstChild) return;
      const contentHeight = (expandoContentRef.current?.firstChild as HTMLElement)?.offsetHeight;
      setCheckoutExpandedHeight(`${contentHeight}px`);
    });
  }, []);

  // Use ResizeObserver to automatically detect height changes in the form content
  useEffect(() => {
    // Only observe when expanded
    if (!checkoutExpanded || !expandoContentRef.current?.firstChild) return;

    const contentElement = expandoContentRef.current.firstChild as HTMLElement;

    // Create ResizeObserver to watch for size changes in the form content
    const resizeObserver = new ResizeObserver(() => {
      updateExpandoHeight();
    });

    // Start observing
    resizeObserver.observe(contentElement);

    // Clean up when unmounting or when checkoutExpanded changes
    return () => {
      resizeObserver.disconnect();
    };
  }, [checkoutExpanded, updateExpandoHeight]);

  // Keep the basic effect for expand/collapse logic
  useEffect(() => {
    if (checkoutExpanded) {
      updateExpandoHeight();
    } else {
      setCheckoutExpandedHeight("0");
    }
  }, [checkoutExpanded, updateExpandoHeight]);

  const setQuantity = (operator: "add" | "remove" = "add", max = 10, min = 1): boolean => {
    const quantity = getProductQuantity(designatedProductId);
    const overMax = operator === "add" && quantity + 1 > max;
    const underMin = operator === "remove" && quantity - 1 < min;
    const calculatedQuantity =
      operator === "add" ? (overMax ? max : quantity + 1) : underMin ? min : quantity - 1;
    const quantityChanged = !overMax && !underMin;

    dispatch({
      type: "SET_PRODUCT_QUANTITY",
      payload: {
        productId: designatedProductId,
        quantity: calculatedQuantity
      }
    });

    return quantityChanged;
  };

  const addQuantity = (): void => {
    // Add Quantity
    const changed = setQuantity("add");

    if (changed) {
      // Fire Analytics
      const product = findProductById(designatedProductId);
      triggerEvent("quantity_add_to_cart", { products: [{ ...product, quantity: 1 }] });
    }
  };

  const removeQuantity = (): void => {
    // Remove Quantity
    const changed = setQuantity("remove");

    if (changed) {
      // Fire Analytics
      const product = findProductById(designatedProductId);
      triggerEvent("quantity_remove_from_cart", { products: [{ ...product, quantity: 1 }] });
    }
  };

  const onCheckoutSuccessCallback = (data) => {
    onCheckoutSuccess(data);
  };

  const getCobrandVal = (lineItems) => {
    const lineItem = lineItems.find(
      ({ lineMeta }) => lineMeta && lineMeta.wvus_product_event_cobrand
    );
    const itemCobrand = lineItem && lineItem?.lineMeta?.wvus_product_event_cobrand;
    const cobrandVal = cobrand || itemCobrand;
    return (cobrandVal && { cobrand_param: cobrandVal }) || {};
  };

  const splitChosenItems = (lineItems: LineItem[]) => {
    /* WHY?
     * Chosen Line Items Does Not Support Multiple Quantity Values
     * We Have To Separate Chosen Line Items From the Other Line Items
     * Then Duplicate Chosen Line Item For Each Quantity.
     */

    // Step 1: Seperate Chosen Items From The Rest Of Line Items
    const [chosenLineItems, restLineItems] = lineItems.reduce(
      ([chosenItems, otherItems], item) => {
        if (CHOSEN_PRODUCT_ID === item.productId) {
          chosenItems.push(item);
        } else {
          otherItems.push(item);
        }

        return [chosenItems, otherItems];
      },
      [[], []]
    );

    // Step 2a: has no chosen line items?
    if (!chosenLineItems.length) {
      return lineItems;
    }

    // Step 2b: Or Get Chosen Product and Quantity
    const [oneChosenItem] = chosenLineItems;
    const chosenQuantity = oneChosenItem?.quantity || 0;

    // Step 3. Duplicate Chosen Line Item With quantity 1 or Keep The One
    const chosenLineItemsSplit =
      chosenQuantity > 1
        ? [...new Array(chosenQuantity)].map(() => ({ ...oneChosenItem, quantity: 1 }))
        : [oneChosenItem];

    // Step 4. Re-combine The Line Items Into One Array
    return [...chosenLineItemsSplit, ...restLineItems];
  };

  /**
   * Scrolls to the credit card heading element if it is not currently in view.
   */
  const scrollToCreditCardHeadingIfNotInView = () => {
    // Timeout allows the DOM to update with the alert message before attempting to calculate viewport.
    setTimeout(() => {
      const ccFieldHeading = document.querySelector(".payment-info-heading.cc-heading");
      scrollToElementIfNotInView(ccFieldHeading);
    }, 100);
  };

  const submitOrderData = async (orderPayload, callback) => {
    try {
      // Dispatch order payload
      const { isValid, requestOrder } = startOrder(orderPayload);
      //We need to first determine whether the order is valid, and NOT a duplicate, via our orderRequest Hook
      if (!isValid()) {
        // If the order is not valid (has duplicate), we need to find the order in state with a matching childID
        const duplicateOrder = duplicateOrderLookup(orderPayload);

        if (duplicateOrder) {
          const { orderId, orderRef } = duplicateOrder.payload || {};
          // Validate orderId and orderRef
          if (!orderId || !orderRef) {
            wvusLogger.log(
              `Duplicate order detected, but orderId or orderRef is missing.`,
              wvusLogger.getLogLevelNames().ERROR
            );
            throw new Error("Duplicate order detected, but orderId or orderRef is missing.");
          }
          // Log duplicate information in console and WVUS Client Logger, send analytics event
          console.warn("Duplicate order detected:", { orderId, orderRef, duplicateOrder });
          wvusLogger.log(
            `Duplicate order detected, orderId: ${orderId}, orderRef: ${orderRef}, duplicateOrder:  ${duplicateOrder}`,
            wvusLogger.getLogLevelNames().ERROR
          );
          triggerEvent("error_view", {
            error_message: "Duplicate Order/isRequesting Error",
            error_type: "app_error",
            event_label: "Flex Checkout dupe validation hook",
            checkout_type: getCheckoutTypeVal(productsRef.current)
          });
          // Redirect to confirmation page using the order data of the original order that we got from state
          // Get lang from query string
          orderRedirect(orderId, orderRef, getQueryParam("lang"));
        } else {
          //this logic SHOULD never trigger, but if it does, we need to log it and handle it
          const err = "Duplicate order detected, but no matching request found in state";
          wvusLogger.log(`${err}`, wvusLogger.getLogLevelNames().ERROR);
          handleOrderError(err, callback, "errors.duplicate_order_error_message");
        }

        return; // Silently kill the request
      }

      dispatch({ type: "SET_ORDER_PAYLOAD", payload: orderPayload });

      // Send order data to API and handle response
      const respData = await requestOrder();
      const data = respData?.data || {};
      validateApiResponse(data); // Validate response

      // Update state on success
      handleOrderSuccess(data, orderPayload, callback);
    } catch (err) {
      // Handle errors
      handleOrderError(err, callback);
    }
  };

  const validateApiResponse = (data) => {
    if (!data?.orderId || !data?.orderRef) {
      throw new Error("Unable to retrieve the order reference.");
    }
  };

  const handleOrderSuccess = (data, orderPayload, callback) => {
    orderInFlightRef.current = false;
    dispatch({ type: "SET_ORDER_RESP", payload: data });
    setSubmitMessagePhraseKey("");

    if (callback) callback(data || {});

    // Handle purchase event and localStorage
    const purchaseEventData = preparePurchaseEventData(data, orderPayload);
    triggerEventOnce("purchase", purchaseEventData);

    storeOrderDetailLocalStorage({
      ...orderPayload,
      ...data,
      eventData: state.eventData
    });

    // Set the ehash cookie
    if (orderPayload?.billingAddress?.email) {
      setEhashCookieAsync(orderPayload.billingAddress.email).catch((error) => {
        console.error("Failed to set ehash cookie:", error);
        wvusLogger.log(error, wvusLogger.getLogLevelNames().ERROR);
      });
    }

    // Call success callback and redirect
    onCheckoutSuccessCallback({ ...orderPayload, ...data, eventData: state.eventData });
    orderRedirect(data.orderId, data.orderRef, getQueryParam("lang"));

    // Final success UI state
    setFormStatus("success");
    setTimeout(() => toggleOverlay(false), 5000);
  };

  const handleIframeError = (error) => {
    handleIframeErrorAsync(error).catch((unexpected) => {
      wvusLogger.log(
        `tokenEx error handler threw: ${toTokenExErrorMessage(unexpected)}`,
        wvusLogger.getLogLevelNames().ERROR
      );
    });
  };

  const handleIframeExpired = () => {
    handleIframeError("TokenEx session expired");
  };

  const handleIframeErrorAsync = async (error) => {
    clearTokenizeWatchdog();
    const message = toTokenExErrorMessage(error);

    const decision = decideTokenExErrorAction({
      message,
      refreshInFlight: expiryRefreshInFlightRef.current,
      attemptsUsed: expiryRefreshCountRef.current,
      msSinceLastAttempt: Date.now() - lastExpiryRefreshAtRef.current,
      lastRefreshSucceeded: lastRefreshSucceededRef.current
    });

    wvusLogger.log(
      `tokenEx error [${decision.action}/${decision.reason}]: ${message}`,
      wvusLogger.getLogLevelNames().ERROR
    );

    const releaseForm = () => {
      // Don't tear down the processing UI mid-order: the card is already tokenized, so an
      // iframe error is noise, and re-arming submit would invite a duplicate click
      if (isRequestingRef.current || orderInFlightRef.current) {
        return;
      }

      toggleOverlay(false);
      setFormStatus("ready");
    };

    if (decision.form === "ready") {
      releaseForm();
    }

    if (decision.action === "ignore") {
      return;
    }

    if (decision.action === "refresh") {
      recordExpiryRefresh();
      expiryRefreshInFlightRef.current = true;
      lastRefreshSucceededRef.current = false;
      pendingExpiryRecoveryRef.current = true;
      wvusLogger.log(
        `tokenEx auth key expired, refreshing config (attempt ${expiryRefreshCountRef.current})`,
        wvusLogger.getLogLevelNames().INFO
      );

      try {
        const refreshed = await refreshTokenExConfig();

        if (refreshed) {
          lastRefreshSucceededRef.current = true;
          releaseForm();
          return;
        }
      } catch (refreshError) {
        wvusLogger.log(
          `tokenEx config refresh threw: ${toTokenExErrorMessage(refreshError)}`,
          wvusLogger.getLogLevelNames().ERROR
        );
      } finally {
        expiryRefreshInFlightRef.current = false;
      }

      // Recovery failed, so donor does need to be told after all
      releaseForm();
    }

    // Any reason other than not_expiry means the key is dead and we have stopped trying to
    // replace it (budget spent, inside the cooldown, or a refresh that failed)
    if (decision.reason === "not_expiry") {
      setCardAlertMessagePhraseKey("errors.tokenex_iframe_error");
    } else {
      setIsCCValid(false);
      setCardType("");
      setCardAlertMessagePhraseKey("errors.tokenex_technical");
    }

    triggerEvent("error_view", {
      error_message: message,
      error_type: "app_error",
      event_label: EXPIRY_OUTCOME_LABELS[decision.reason] ?? "TokenEx",
      checkout_type: getCheckoutTypeVal(productsRef.current)
    });
  };

  const handleIframeLoadError = (error) => {
    const message = toTokenExErrorMessage(error);

    wvusLogger.log(`tokenEx error: ${message}`, wvusLogger.getLogLevelNames().ERROR);
    toggleOverlay(false);
    setFormStatus("ready");
    setTokenExErrorPhraseKey("errors.tokenex_cc_unavailable");
    triggerEvent("error_view", {
      error_message: message,
      error_type: "app_error",
      event_label: "TokenEx",
      checkout_type: getCheckoutTypeVal(productsRef.current)
    });
  };

  const handleIframeInitTimeout = () => {
    wvusLogger.log(
      `tokenEx iframe did not load within ${TOKENEX_INIT_TIMEOUT_MS}ms`,
      wvusLogger.getLogLevelNames().WARNING
    );
  };

  const handleIframeLoaded = () => {
    setTokenExErrorPhraseKey(null);
    setCardAlertMessagePhraseKey((previous) =>
      previous === "errors.tokenex_technical" || previous === "errors.tokenex_iframe_error"
        ? ""
        : previous
    );
  };

  const handleIframeLoadDeadline = () => {
    wvusLogger.log(
      `tokenEx iframe never loaded within ${TOKENEX_LOAD_DEADLINE_MS}ms`,
      wvusLogger.getLogLevelNames().ERROR
    );
    setCardAlertMessagePhraseKey("errors.tokenex_technical");
    triggerEvent("error_view", {
      error_message: `TokenEx iframe never loaded within ${TOKENEX_LOAD_DEADLINE_MS}ms`,
      error_type: "app_error",
      event_label: "TokenEx:load_deadline",
      checkout_type: getCheckoutTypeVal(productsRef.current)
    });
  };

  const preparePurchaseEventData = (data, orderPayload) => {
    return {
      products: state.products,
      wc_order_id: data?.orderId,
      order_ref: data?.orderRef,
      campaign_code: orderPayload.campaignId,
      payment_method_option: mapPaymentMethod(orderPayload.paymentInfo.paymentMethod),
      env: getOrderApiEnv(),
      ...(showSmsPreference ? { sms_permission: smsPreference ? "true" : "false" } : {})
    };
  };

  const mapPaymentMethod = (method) => {
    const methodMap = {
      creditcard: "credit_card",
      wp_applepay: "apple_pay"
    };
    return methodMap[method.toLowerCase()] || method;
  };

  const handleOrderError = (
    err,
    callback,
    errorMessagePhraseKey: PhraseKey = "errors.general_error_volunteer"
  ) => {
    orderInFlightRef.current = false;
    console.error(err);
    if (callback) callback({ error: err });
    dispatch({ type: "ADD_ORDER_ERROR_RESP", payload: err });
    toggleOverlay(false);

    if (isPaymentAuthError(err)) {
      handlePaymentAuthError(err);
    } else {
      handleGenericOrderError(err, errorMessagePhraseKey);
    }

    setFormStatus("ready");
  };

  const handlePaymentAuthError = (err) => {
    const { responseCode, worldPayOrderId, message = "" } = err.response;

    triggerEvent("error_view", {
      error_message: message,
      error_type: "payment_error",
      event_label: `${worldPayOrderId}-${responseCode}`,
      checkout_type: getCheckoutTypeVal(productsRef.current)
    });

    setIsCCValid(false);
    setValidationErrorCCPhraseKey("errors.rta_field_validation");
    setCardAlertMessagePhraseKey("errors.rta_error");
    scrollToCreditCardHeadingIfNotInView();
  };

  const handleGenericOrderError = (
    err,
    errorMessagePhraseKey: PhraseKey = "errors.general_error_volunteer"
  ) => {
    const errorDetail = err.toString();

    setSubmitMessagePhraseKey(errorMessagePhraseKey);

    wvusLogger.log(`requestOrderApi error: ${errorDetail}`, wvusLogger.getLogLevelNames().ERROR);

    triggerEvent("error_view", {
      error_message: getPhrase(errorMessagePhraseKey),
      error_type: "create_order_error",
      event_label: errorDetail,
      checkout_type: getCheckoutTypeVal(productsRef.current)
    });
  };

  const setOrderPayload = useCallback(
    async (data, callback = null) => {
      const { paymentInfo, billingAddress } = data;
      const timestamp = new Date().toISOString();
      const mappedLineItems = opts.mapProducts(products, state);
      const cobrandParam = getCobrandVal(mappedLineItems);
      const updatedLineItems = onCheckoutLineItemsMap(mappedLineItems, { ...data, timestamp }); // TODO: merge the functionality of opts.mapProducts and this function.
      const lineItems = splitChosenItems(updatedLineItems);

      // The SAK config is present only for artist tours. We pull from the window
      // rather than appSettings in case it has been modified for A/B testing.
      const sakConfig = window.wvusFlexCheckoutSettings?.sakConfig;

      // Attempt to retrieve the reCaptcha token
      await getRecaptchaToken(opts.checkoutId)
        .then((reCaptchaToken) => reCaptchaToken)
        .catch((error) => {
          console.error(error);
          wvusLogger.log(
            `failure to get reCaptcha token:  ${error}, submitting order without reCaptcha`,
            wvusLogger.getLogLevelNames().ERROR
          );
        })
        .then((reCaptchaToken) => {
          const csscValue = getCampaignVal();

          // Log error if CSSC is empty when submitting order
          if (!csscValue || csscValue.trim() === "") {
            wvusLogger.log(
              "Empty CSSC detected during order submission.",
              wvusLogger.getLogLevelNames().ERROR
            );
          }

          const orderPayload = {
            timestamp,
            lineItems,
            campaignId: csscValue,
            billingAddress,
            paymentInfo,
            blueConicId: getBlueConicProfileId(),
            ...(reCaptchaToken ? { reCaptchaToken } : {}), // Optionally add ReCaptcha Token
            orderMeta: {
              // Order Meta
              orderSource: window.location.href,
              checkoutId: opts.checkoutId,
              ...(showSmsPreference
                ? { _sms_preferences: smsPreference ? "send" : "no-send" }
                : {}), // Include SMS Preference
              ...cobrandParam // Include Optional Cobrand Order Meta
            },
            ...(sakConfig ? { sakConfig } : {}) // Add SAK config if present
          };
          submitOrderData(orderPayload, callback);
        });
    },
    [state, smsPreference]
  );

  const getCCFields = (fields): CcFields => {
    const ccFieldKeys = ["expirationDate"];

    return ccFieldKeys.reduce((ccFields, key) => {
      ccFields[key] = fields[key];
      return ccFields;
    }, {});
  };

  const onTokenize = (data) => {
    if (refOnTokenize?.current) {
      refOnTokenize.current(data);
    }
  };

  /** Resolves true once a replacement config is in state, false if the request failed. */
  const refreshTokenExConfig = async () => {
    const iframeConfigRefresh = tokenExConfigQuery?.refresh || (() => Promise.resolve(false));

    try {
      return await iframeConfigRefresh();
    } catch (error) {
      const message = toTokenExErrorMessage(error);
      console.error("TokenEx iframe refresh failed", message);
      wvusLogger.log(
        `TokenEx iframe refresh failed: ${message}`,
        wvusLogger.getLogLevelNames().ERROR
      );
      triggerEvent("error_view", {
        error_message: "TokenEx iframe refresh failed",
        error_type: "tokenex_error",
        event_label: message,
        checkout_type: getCheckoutTypeVal(productsRef.current)
      });
      setSubmitMessagePhraseKey("errors.tokenex_technical");

      return false;
    }
  };

  const onValidate = useCallback(
    (data) => {
      const ACCEPTED_CARDS = {
        visa: "VISA",
        americanExpress: "AMEX",
        discover: "DSCV",
        diners: "DSCV",
        jcb: "DSCV",
        masterCard: "MAST"
      };
      const isAcceptedCard = data.cardType && Object.keys(ACCEPTED_CARDS).includes(data.cardType);
      const isValid = data.isValid && isAcceptedCard;

      setIsCCValid(isValid);

      if (!isValid) {
        setValidationErrorCCPhraseKey("errors.valid_credit_card");
      } else {
        setCardType(ACCEPTED_CARDS[data.cardType]);
        triggerEventOnce("add_payment_info", { products: state.products });
        setSubmitMessagePhraseKey("");
        setCardAlertMessagePhraseKey("");
        // A card that validates proves iframe is working and a donor is progressing,
        // so any attempts spent recovering from an earlier expiry are returned.
        resetExpiryRefreshBudget();
      }
    },
    [setIsCCValid, setValidationErrorCCPhraseKey, state.products]
  );

  const onBlur = useCallback(() => {
    setIsCardFocused(false);
    setSecondInteration(true);
  }, [setSecondInteration]);

  const onFocus = () => {
    setIsCardFocused(true);
    if (
      validationErrorCCPhraseKey === "errors.tokenex_technical" ||
      validationErrorCCPhraseKey === "errors.tokenex_inactive"
    ) {
      setSecondInteration(false);
      setValidationErrorCCPhraseKey("");
    }
  };

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();
      setSecondInteration(true);
      setFormStatus("loading");
      setSubmitMessagePhraseKey("");
      setCardAlertMessagePhraseKey("");
      const { getFormState, validateForm } = formMethods;
      const formState = getFormState();
      const fields = formState.fields;
      const ccFields = getCCFields(fields);
      const isValidated = validateForm();

      if (!isCCValid) {
        setValidationErrorCCPhraseKey((previous) =>
          previous === "errors.tokenex_inactive" ? previous : "errors.valid_credit_card"
        );
      }

      // Cancel If Not Valid
      if (!isValidated || !isCCValid) {
        setFormStatus("ready");

        return false;
      }

      refOnTokenize.current = (tokenExData) => {
        clearTokenizeWatchdog();
        orderInFlightRef.current = true;
        const { expirationDate } = ccFields;
        const [expirationMonth, expirationYear] = expirationDate.value
          .split("/")
          .map((val) => parseInt(val, 10));
        const { cardType, token, tokenHMAC } = tokenExData;
        const lastFour = `${token}`.substr(-4);
        toggleOverlay(true);

        const billingAddress = {
          address1: fields.address1.value,
          address2: fields?.address2?.value ?? "",
          city: fields.city.value,
          state: fields.state.value,
          postcode: fields.postcode.value,
          firstName: fields.firstName.value,
          lastName: fields.lastName.value,
          email: fields.email.value,
          phone: toDigitsOnly(fields.phone.value)
        };

        const paymentInfo = {
          paymentMethod: "creditcard",
          tokenType: "TokenEx",
          token: token,
          tokenHMAC: tokenHMAC,
          ccType: cardType,
          expirationMonth: expirationMonth,
          expirationYear: expirationYear,
          lastFour,
          allowAuth
        };

        setOrderPayload({
          billingAddress,
          paymentInfo,
          photoConsent,
          showPhotoConsentOption
        })?.catch((error) => {
          orderInFlightRef.current = false;
          wvusLogger.log(
            `Order payload preparation failed: ${toTokenExErrorMessage(error)}`,
            wvusLogger.getLogLevelNames().ERROR
          );
          setFormStatus("ready");
          setSubmitMessagePhraseKey("errors.tokenex_technical");
        });
      };

      try {
        refIframe?.current && refIframe.current.tokenize();
        startTokenizeWatchdog();
      } catch (error) {
        wvusLogger.log(`Tokenization failed: ${error}`, wvusLogger.getLogLevelNames().ERROR);
        setFormStatus("error");
        setSubmitMessagePhraseKey("errors.tokenex_technical");
        if (failedTokenizeRefreshCount < 1) {
          // this is arbitrary, but we don't want to refresh the iframe too many times
          // Fire and forget: the config effect handles the outcome, and this catch has
          // already put the form into its error state.
          void refreshTokenExConfig();
        } else {
          const message = toTokenExErrorMessage(error);
          wvusLogger.log(`tokenEx error: ${message}`, wvusLogger.getLogLevelNames().ERROR);
          setTokenExErrorPhraseKey("errors.tokenex_cc_unavailable");
          triggerEvent("error_view", {
            error_message: message,
            error_type: "app_error",
            event_label: "TokenEx",
            checkout_type: getCheckoutTypeVal(productsRef.current)
          });
        }
        setFailedTokenizeRefreshCount(failedTokenizeRefreshCount + 1);
      }
    },
    [formMethods, formStatus, photoConsent, smsPreference, isCCValid, failedTokenizeRefreshCount]
  );

  const handleFormFieldInteraction = useCallback(() => {
    triggerEventOnce("add_to_cart", { products });
    triggerEventOnce("begin_checkout", { products });
  }, [products]);

  /**
   * Tracks form field changes using debounce logic to catch browser auto-fill of multiple fields,
   * so that the form can be expanded and validated without depending on the user to trigger validation
   * by moving normally through the fields.
   *
   * @param {React.ChangeEvent<HTMLElement>} event - The change event triggered by a form field.
   */
  const handleFormFieldChange = useCallback(
    (event: React.ChangeEvent<HTMLElement>) => {
      // This is how long to wait after the last form field change before counting how many fields have changed.
      // It should be just long enough that auto-fill can complete before the debounce.
      // It should be short enough that a user tabbing through fields will exceed it between field changes.
      const debounceDelay = 250;

      // If the field is expirationDate but it doesn't currently have focus, that tells us the credit card
      // number was auto-filled. We need to move focus to the expiration date field to blur focus from the
      // CC field and trigger validation. Otherwise the submit button will remain disabled.
      if (event.target.id === "expirationDate" && document.activeElement?.id !== "expirationDate") {
        document.getElementById("expirationDate")?.focus();

        // Sets a session flag for analytics purposes.
        setBrowserAutoFillUsed();
      }

      setFormChangeState((prevState) => {
        // To properly debounce, we need to reset the timeout countdown on each change.
        clearTimeout(prevState.debounceTimeout);

        // Only count each field once. Otherwise, normal use will yield 3-5 change events per field.
        const changedFields = new Set(prevState.changedFields);
        changedFields.add(event.target.id);

        // Keep the value of the address1 field in sync with the searchaddress field.
        if (event.target.id === "searchaddress") {
          formMethods.getFormState().fields.address1.value = (
            event.target as HTMLInputElement
          ).value;
        }

        // Evaluate number of field changes `debounceDelay` milliseconds after they stop firing and not before.
        const debounceTimeout = setTimeout(() => {
          // If more than 1 field has changed, expand and validate the whole form.
          if (changedFields.size > 1) {
            // Sets a session flag for analytics purposes.
            setBrowserAutoFillUsed();

            // Validate all input fields except address and credit card.
            formMethods.validateForm();

            // Preserve preferred single-line address input unless address was auto-filled.
            if (changedFields.has("address1") || changedFields.has("searchaddress")) {
              setToggleFlag("on");
              // Recalculate height when address form is shown due to autofill
              if (checkoutExpanded) {
                updateExpandoHeight();
              }
            } else {
              // If address was not auto-filled, we can assume it is blank.
              document.querySelector(".address-section__autocomplete")?.classList.add("has-error");
            }

            // If the CC isn't validated it's empty and thus invalid. We can't interrogate it's value directly.
            const ccNumContainer = document.querySelector(
              "[data-field-container=wvus_new_cc_number]"
            );
            if (!ccNumContainer?.classList.contains("has-success")) {
              ccNumContainer.classList.add("has-error");
            }
          }

          // Reset form change state after debounce.
          setFormChangeState({
            changedFields: new Set(),
            debounceTimeout: null
          });
        }, debounceDelay);

        return {
          changedFields,
          debounceTimeout
        };
      });
    },
    [formMethods, updateExpandoHeight, checkoutExpanded]
  );

  const checkoutFieldProps = {
    formMethods
  };

  return (
    <>
      <QuantityControl
        disabled={!step2.enabled}
        addQuantity={addQuantity}
        removeQuantity={removeQuantity}
        quantity={productQuantity}
        visible={allowMultipleQuantity}
      />

      <HorizontalRule visible={allowMultipleQuantity} />

      <PaymentHeader
        toggleClick={() => {
          setCheckoutExpanded(!checkoutExpanded);
        }}
        disabled={!step2.enabled || tokenExErrorPhraseKey}
        checkoutExpanded={checkoutExpanded}
        setOrderPayload={setOrderPayload}
        hasProduct={hasProduct}
        formEnabled={isFormEnabled}
        mainState={state}
        isKioskMode={isKioskMode}
        tokenExErrorPhraseKey={tokenExErrorPhraseKey}
      />

      <div
        ref={expandoContentRef}
        className={`expando ${checkoutExpanded ? "expando-open" : "expando-close"}`}
        style={{
          height: checkoutExpanded ? checkoutExpandedHeight : "0",
          overflow: "hidden",
          transition: "height 0.3s ease-in-out"
        }}>
        <FieldsGroup
          visible={hasProduct}
          className="form-fields__wrapper"
          loading={formStatus === "loading"}
          disabled={!isFormEnabled}
          onFormFieldInteraction={handleFormFieldInteraction}
          onFormFieldChange={handleFormFieldChange}>
          <fieldset className="form-fields--beside">
            <FirstNameField {...checkoutFieldProps} />
            <LastNameField {...checkoutFieldProps} />
          </fieldset>
          <EmailField {...checkoutFieldProps} />
          <PhoneField {...checkoutFieldProps} />
          <CreditCardField
            {...checkoutFieldProps}
            refIframe={refIframe}
            isCCValid={isCCValid}
            secondInteraction={secondInteraction}
            setTokenExError={setTokenExErrorPhraseKey}
            handleIframeError={handleIframeError}
            handleIframeLoadError={handleIframeLoadError}
            handleIframeInitTimeout={handleIframeInitTimeout}
            initTimeout={TOKENEX_INIT_TIMEOUT_MS}
            handleIframeLoadDeadline={handleIframeLoadDeadline}
            handleIframeLoaded={handleIframeLoaded}
            loadDeadline={TOKENEX_LOAD_DEADLINE_MS}
            fieldAlertMessagePhraseKey={cardAlertMessagePhraseKey}
            validationErrorCCPhraseKey={validationErrorCCPhraseKey}
            tokenExConfigQuery={{ ...tokenExConfigQuery, data: appliedConfig }}
            onTokenize={onTokenize}
            onExpire={handleIframeExpired}
            onValidate={onValidate}
            onBlur={onBlur}
            onFocus={onFocus}
            cardType={cardType}
          />
          {/* Hidden field prevents Safari mobile form auto-complete from auto-focusing on the expiration date field */}
          <input
            type="checkbox"
            style={{ visibility: "hidden", position: "absolute", transform: "scale(.01)" }}
          />
          <ExpirationDateField {...checkoutFieldProps} />
          <AddressAutoComplete
            formMethods={formMethods}
            visible={toggleFlag !== "on"}
            {...checkoutFieldProps}
            setToggleFlag={setToggleFlag}
          />
          <AddressToggle
            toggleFlag={toggleFlag}
            setToggleFlag={(flag) => {
              setToggleFlag(flag);
              // When address form is expanded/collapsed, recalculate expando height
              if (checkoutExpanded) {
                updateExpandoHeight();
              }
            }}
          />
          <AddressGroup visible={toggleFlag === "on"}>
            <AddressLine1Field {...checkoutFieldProps} />
            <AddressLine2Field {...checkoutFieldProps} />
            <CityField {...checkoutFieldProps} />
            <fieldset className="form-fields--beside">
              <StateField {...checkoutFieldProps} />
              <PostalCodeField {...checkoutFieldProps} />
            </fieldset>
          </AddressGroup>
          {appSettings.appType === "artist-tour" && (
            <>
              {/* Show error messages inline for artist-tours */}
              {submitMessagePhraseKey && (
                <SubmitMessage
                  messagePhraseKey={submitMessagePhraseKey}
                  alertType="danger"
                  wrapperClassName=""
                  visible={true}
                />
              )}
              <SubmitButton
                className="order-summary__submit"
                onSubmit={handleSubmit}
                disabled={!isFormEnabled}>
                <Say phrase={ctaPhraseKey} />
              </SubmitButton>
            </>
          )}
        </FieldsGroup>
      </div>

      {/* Show error messages after the expando for "chosen" app type */}
      {appSettings.appType === "chosen" && submitMessagePhraseKey && (
        <SubmitMessage
          messagePhraseKey={submitMessagePhraseKey}
          alertType="danger"
          className=""
          visible={true}
        />
      )}

      <SmsOptIn
        visible={showSmsPreference}
        checked={smsPreference}
        onToggle={(enabled) => setSmsPreference(enabled)}
      />

      {appSettings.appType === "artist-tour" && <TourOrderSummary products={products} />}
      {appSettings.appType === "chosen" && (
        <ChosenOrderSummary
          products={products}
          formEnabled={formEnabled}
          handleSubmit={handleSubmit}
        />
      )}
      <PhotoConsentOption
        checked={showPhotoConsentOption && photoConsent}
        onChange={(value) => setPhotoConsent(value)}
        visible={showPhotoConsentOption}
      />
    </>
  );
});

export default Checkout;
